import { operationalLocationCode } from "@integrator-src/location/operational-location-code";
import {
  operationalLocationSelectLabel,
  operationalLocationSelectRank,
  sortTransferEndpointLocations,
  type TransferEndpointLocationRow,
} from "@/lib/transferEndpointLocations";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";

export const OPERATIONAL_LOCATION_FILTER_DIVIDER_VALUE = "__operational_location_divider__";

/** Filter token matching the purchase-order / incoming SQL (lowercased operational code). */
export function operationalLocationFilterCode(location: TransferEndpointLocationRow): string {
  return operationalLocationCode({
    publicLocationId: location.id,
    mrpSiteCode: location.mrp_site_code,
    abbreviation: location.abbreviation,
  }).trim().toLowerCase();
}

export function operationalLocationFilterOptions(
  locations: readonly TransferEndpointLocationRow[],
): Array<{ value: string; label: string; locationId: number; kind?: SearchableSelectOption["kind"]; disabled?: boolean }> {
  const seen = new Set<string>();
  const options: Array<{ value: string; label: string; locationId: number; kind?: SearchableSelectOption["kind"]; disabled?: boolean }> = [];
  let hasPrimary = false;
  let dividerInserted = false;
  for (const location of sortTransferEndpointLocations(locations)) {
    const rank = operationalLocationSelectRank(location);
    if (rank < 100) hasPrimary = true;
    if (rank >= 100 && hasPrimary && !dividerInserted) {
      options.push({
        value: OPERATIONAL_LOCATION_FILTER_DIVIDER_VALUE,
        label: "Other locations",
        locationId: -1,
        kind: "divider",
        disabled: true,
      });
      dividerInserted = true;
    }
    const value = operationalLocationFilterCode(location);
    if (value === "" || seen.has(value)) continue;
    seen.add(value);
    options.push({
      value,
      label: operationalLocationSelectLabel(location),
      locationId: location.id,
    });
  }
  return options;
}

/** Comma-separated operational warehouse codes from an Incoming parts `location=` query. */
export function parseLocationSearchParam(raw: string | null | undefined): string[] {
  if (raw == null || raw.trim() === "") return [];
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const piece of raw.split(",")) {
    const code = piece.trim().toLowerCase();
    if (code === "" || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

/**
 * Query `location=` value for a multi-select warehouse filter.
 * All selected (or locations not loaded yet) omits the param so unknown/null ship-tos stay visible.
 * Empty selection sends a token that matches no warehouse.
 */
export function locationFilterQueryParam(
  selected: ReadonlySet<string>,
  availableCodes: readonly string[],
): string | undefined {
  if (availableCodes.length === 0) return undefined;
  if (selected.size === 0) return "__none__";
  const allOn =
    availableCodes.every((code) => selected.has(code)) && selected.size >= availableCodes.length;
  if (allOn) return undefined;
  return [...selected].sort().join(",");
}

export function publicLocationIdForFilterCode(
  code: string,
  options: readonly { value: string; locationId: number }[],
): number | null {
  const needle = code.trim().toLowerCase();
  return options.find((option) => option.value === needle)?.locationId ?? null;
}

export type OperationalLocationResolved = {
  filterCode: string;
  displayName: string;
  locationId: number;
};

/** Full warehouse name for filter chips (not abbreviation-only labels). */
export function operationalLocationDisplayName(location: TransferEndpointLocationRow): string {
  const name = location.name?.trim() ?? "";
  if (name !== "") {
    return name;
  }
  const abbreviation = location.abbreviation?.trim() ?? "";
  if (abbreviation !== "") {
    return abbreviation;
  }
  return `Location ${String(location.id)}`;
}

/** Map lowercase tokens (abbreviation, name, MRP site code, filter code) to one hub warehouse. */
export function buildOperationalLocationResolveIndex(
  locations: readonly TransferEndpointLocationRow[],
): Map<string, OperationalLocationResolved> {
  const index = new Map<string, OperationalLocationResolved>();
  for (const location of locations) {
    const filterCode = operationalLocationFilterCode(location);
    if (filterCode === "") {
      continue;
    }
    const resolved: OperationalLocationResolved = {
      filterCode,
      displayName: operationalLocationDisplayName(location),
      locationId: location.id,
    };
    const tokens = [
      filterCode,
      location.mrp_site_code?.trim().toLowerCase(),
      location.abbreviation?.trim().toLowerCase(),
      location.name?.trim().toLowerCase(),
    ];
    for (const token of tokens) {
      if (token != null && token !== "" && !index.has(token)) {
        index.set(token, resolved);
      }
    }
  }
  return index;
}

export function resolveOperationalLocationToken(
  raw: string,
  index: ReadonlyMap<string, OperationalLocationResolved>,
): OperationalLocationResolved | null {
  const token = raw.trim().toLowerCase();
  if (token === "") {
    return null;
  }
  return index.get(token) ?? null;
}

export function resolveOperationalLocationTokenOrFallback(
  raw: string,
  index: ReadonlyMap<string, OperationalLocationResolved>,
): OperationalLocationResolved {
  const resolved = resolveOperationalLocationToken(raw, index);
  if (resolved != null) {
    return resolved;
  }
  const trimmed = raw.trim();
  const filterCode = trimmed.toLowerCase();
  return {
    filterCode,
    displayName: trimmed === "" ? filterCode : trimmed,
    locationId: -1,
  };
}
